import { useEffect, useRef } from 'react'
import type { HotelAvailabilityParams } from './type'
import { mappedPrices } from './use-hotel-availability-search'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'
import { useHotelAvailabilitySearchStore } from '~/stores/hotel-availability-search'

const API_BASE_URL = process.env.NEXT_PUBLIC_INV_URL

const fetchAvailability = async (params: HotelAvailabilityParams) => {
  const queryParams = new URLSearchParams()
  queryParams.append('hotelId', params.hotelId)
  queryParams.append('fromDate', params.fromDate)
  queryParams.append('toDate', params.toDate)
  if (params.adults !== undefined) queryParams.append('adults', params.adults.toString())
  if (params.children !== undefined) queryParams.append('children', params.children.toString())

  const response = await fetch(`${API_BASE_URL}/cm/inventory/hotel-available?${queryParams}`)
  if (!response.ok) throw new Error('Failed to fetch availability')
  const result = await response.json()
  return result.data
}

const fetchAvailabilitySearch = async (params: HotelAvailabilityParams) => {
  const queryParams = new URLSearchParams()
  queryParams.append('hotelId', params.hotelId)
  queryParams.append('fromDate', params.fromDate)
  queryParams.append('toDate', params.toDate)
  queryParams.append('adults', params.adults?.toString() || '1')
  queryParams.append('children', params.children?.toString() || '0')

  const response = await fetch(`${API_BASE_URL}/cm/inventory/hotel-available/search?${queryParams}`)
  if (!response.ok) throw new Error('Failed to fetch availability search')
  const result = await response.json()
  return result.data
}

export function useHotelAvailability(initialParams?: HotelAvailabilityParams) {
  const {
    availability,
    availabilitySearch,
    isLoading,
    isError,
    setAvailability,
    setLoading,
    setError,
  } = useHotelAvailabilityStore()
  const { setMappedPrices } = useHotelAvailabilitySearchStore()

  const prevParamsRef = useRef<HotelAvailabilityParams | null>(null)
  const currentParamsRef = useRef<HotelAvailabilityParams | undefined>(initialParams)

  const isParamsChanged = (newParams: HotelAvailabilityParams | undefined) => {
    const prev = prevParamsRef.current
    if (!prev || !newParams) return true
    return (
      prev.hotelId !== newParams.hotelId ||
      prev.fromDate !== newParams.fromDate ||
      prev.toDate !== newParams.toDate ||
      prev.adults !== newParams.adults ||
      prev.children !== newParams.children
    )
  }

  const fetchData = async (overrideParams?: HotelAvailabilityParams, isManualRefetch = false) => {
    const effectiveParams = overrideParams || currentParamsRef.current
    if (!effectiveParams?.hotelId || !effectiveParams.fromDate || !effectiveParams.toDate) {
      setAvailability([])
      setLoading(false)
      return
    }
    if (new Date(effectiveParams.fromDate) > new Date(effectiveParams.toDate)) {
      setLoading(false)
      return
    }

    if (!isManualRefetch && !isParamsChanged(effectiveParams)) return
    prevParamsRef.current = effectiveParams
    currentParamsRef.current = effectiveParams

    setLoading(true)
    setError(false)

    const availabilityPromise = fetchAvailability(effectiveParams)
    const searchPromise = fetchAvailabilitySearch(effectiveParams)

    try {
      const availabilityData = await availabilityPromise
      setAvailability(availabilityData)
      setLoading(false)

      searchPromise
        .then((searchData) => {
          setMappedPrices(
            mappedPrices(searchData, effectiveParams.fromDate, effectiveParams.toDate)
          )
        })
        .catch((err) => {
          console.error('Error fetching availability search:', err)
        })
    } catch (err) {
      console.error('Error fetching availability:', err)
      setError(true)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [
    initialParams?.hotelId,
    initialParams?.fromDate,
    initialParams?.toDate,
    initialParams?.adults,
    initialParams?.children,
  ])

  return {
    availability,
    availabilitySearch,
    isLoading,
    isError,
    refetch: (newParams?: HotelAvailabilityParams) => fetchData(newParams, true),
  }
}
